import { supabase } from './supabaseClient'

export async function getCurrentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getUser()
  return data.user?.id ?? null
}

export async function createReservation(params: {
  equipmentId: number
  organizationId?: number
  contactId?: number
  purpose: 'poc' | 'test' | 'tech_eval' | 'maintenance'
  projectName?: string
  startAt: string
  endAt: string
  notes?: string
}) {
  const userId = await getCurrentUserId()
  const durationMs = new Date(params.endAt).getTime() - new Date(params.startAt).getTime()
  const maxMs = 14 * 24 * 60 * 60 * 1000
  if (durationMs <= 0) throw new Error('End must be after start')
  if (durationMs > maxMs) throw new Error('Reservation exceeds 14 day limit')
  const payload = {
    equipment_id: params.equipmentId,
    created_by: userId,
    sales_engineer_id: userId,
    organization_id: params.organizationId ?? null,
    contact_id: params.contactId ?? null,
    purpose: params.purpose,
    project_name: params.projectName ?? null,
    start_at: params.startAt,
    end_at: params.endAt,
    notes: params.notes ?? null,
  }
  const { error } = await supabase.schema('app' as any).from('reservations').insert(payload)
  if (error) throw error
}

export async function createCheckout(params: {
  equipmentId: number
  organizationId?: number
  contactId?: number
  purpose: 'poc' | 'test' | 'tech_eval' | 'maintenance'
  projectName?: string
  watchlist?: boolean
  usageNotes?: string
  productIds?: number[]
  expectedReturnAt?: string
  conditionOut?: string
}) {
  const userId = await getCurrentUserId()
  if (!userId) throw new Error('Must be signed in')
  const checkout = {
    equipment_id: params.equipmentId,
    sales_engineer_id: userId,
    organization_id: params.organizationId ?? null,
    contact_id: params.contactId ?? null,
    purpose: params.purpose,
    project_name: params.projectName ?? null,
    watchlist: Boolean(params.watchlist),
    usage_notes: params.usageNotes ?? null,
    expected_return_at: params.expectedReturnAt ?? null,
    condition_out: params.conditionOut ?? null,
    created_by: userId,
    updated_by: userId,
  }
  const { data, error } = await supabase
    .schema('app' as any).from('checkouts')
    .insert(checkout)
    .select('id')
    .single()
  if (error) throw error
  const checkoutId = data.id as number
  if (params.productIds && params.productIds.length) {
    const rows = params.productIds.map((pid) => ({ checkout_id: checkoutId, product_id: pid }))
    const { error: e2 } = await supabase.schema('app' as any).from('checkout_products').insert(rows)
    if (e2) throw e2
  }
  return { id: checkoutId }
}

export async function closeCheckout(params: {
  checkoutId: number
  outcome?: string
  conditionIn?: string
}) {
  const userId = await getCurrentUserId()
  const { error } = await supabase
    .schema('app' as any).from('checkouts')
    .update({
      ended_at: new Date().toISOString(),
      outcome: params.outcome ?? null,
      condition_in: params.conditionIn ?? null,
      updated_by: userId,
    })
    .eq('id', params.checkoutId)
  if (error) throw error
}

export async function getOpenCheckoutId(equipmentId: number): Promise<number | null> {
  const { data, error } = await supabase
    .schema('app' as any).from('checkouts')
    .select('id')
    .eq('equipment_id', equipmentId)
    .is('ended_at', null)
    .eq('status', 'open')
    .maybeSingle()
  if (error) throw error
  return data?.id ?? null
}

export async function listReservationsInRange(startIso: string, endIso: string) {
  const { data, error } = await supabase
    .from('reservations_v')
    .select('*')
    .gte('end_at', startIso)
    .lte('start_at', endIso)
  if (error) throw error
  return data || []
}

export type CheckoutLocation = {
  id: string
  label: string
  lat: number
  lon: number
  state?: string
  city?: string
  status?: string
  expectedReturnAt?: string | null
}

/**
 * Returns current checked-out equipment locations for mapping.
 * Expects a Postgres function or view named `checked_out_locations_v` that yields
 * rows with: id, equipment_name, latitude, longitude, state, city
 * This function is resilient: if the RPC/view is missing, it returns an empty array.
 */
export async function listCheckedOutLocations(): Promise<CheckoutLocation[]> {
  try {
    const { data, error } = await supabase.rpc('checked_out_locations_v')
    if (error) return []
    const rows = Array.isArray(data) ? data : []
    return rows
      .map((r: any) => ({
        id: String(r.id ?? r.equipment_id ?? crypto.randomUUID()),
        label: String(r.equipment_name ?? r.name ?? 'Item'),
        lat: Number(r.latitude ?? r.lat),
        lon: Number(r.longitude ?? r.lon ?? r.lng),
        state: r.state ?? r.region ?? null,
        city: r.city ?? null,
        status: r.status ?? null,
        expectedReturnAt: r.expected_return_at ?? null,
      }))
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon))
  } catch {
    return []
  }
}

// === App Settings: key-value config from app.settings ===

/**
 * Fetch a single setting by key from app.settings.
 * Returns the JSONB `value` column, or null if not found.
 */
export async function getSetting<T = any>(key: string): Promise<T | null> {
  try {
    const { data, error } = await supabase
      .schema('app')
      .from('settings')
      .select('value')
      .eq('key', key)
      .maybeSingle()
    if (error || !data) return null
    return data.value as T
  } catch {
    return null
  }
}

/**
 * Update (upsert) a setting in app.settings.
 * Requires admin role (RLS enforced).
 */
export async function updateSetting(key: string, value: any): Promise<void> {
  const { error } = await supabase
    .schema('app')
    .from('settings')
    .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: 'key' })
  if (error) throw error
}

/** Typed warehouse address as stored in app.settings → warehouse_address */
export type WarehouseAddress = {
  contact_name?: string
  company?: string
  line1: string
  line2?: string
  city: string
  state?: string
  postal_code?: string
  country?: string
  phone?: string
  email?: string
}

/**
 * Fetch the warehouse ship-from address from app.settings.
 * Falls back to a sensible default if the setting is missing.
 */
export async function getWarehouseAddress(): Promise<WarehouseAddress> {
  const stored = await getSetting<WarehouseAddress>('warehouse_address')
  if (stored && stored.line1 && stored.city) return stored
  // Fallback default — should be overridden in admin settings
  return {
    contact_name: 'Routr Warehouse',
    company: 'Routr',
    line1: '1 Market St',
    city: 'San Francisco',
    state: 'CA',
    postal_code: '94105',
    country: 'US',
  }
}

// === Shipping: client wrappers for Edge Functions ===
export type ShippingAddress = {
  contact_name?: string
  company?: string
  email?: string
  phone?: string
  line1: string
  line2?: string
  city: string
  state?: string
  postal_code?: string
  country?: string
}

export type ShippingParcel = {
  length_cm?: number
  width_cm?: number
  height_cm?: number
  weight_kg?: number
  preset?: string
}

export async function fetchShippingRates(params: {
  from: ShippingAddress
  to: ShippingAddress
  parcel: ShippingParcel
  carriers?: string[]
  serviceLevels?: string[]
}) {
  const { data, error } = await supabase.functions.invoke('shipping-rates', {
    body: params,
  })
  if (error) throw error
  return data as {
    ok: boolean
    rates: Array<{
      carrier: string
      service: string
      etaDays: number
      costCents: number
      currency: string
      rateId: string
    }>
    meta: {
      shippoConfigured?: boolean
      upsConfigured?: boolean
      fedexConfigured?: boolean
      uspsConfigured?: boolean
      carrierAccounts?: string[]
      shippoMessages?: any[]
    }
  }
}

/**
 * Validate a shipping address using Shippo's address validation API.
 * Returns validation result with is_valid flag, messages, and suggested corrections.
 */
export async function validateShippingAddress(address: ShippingAddress) {
  const { data, error } = await supabase.functions.invoke('shipping-validate-address', {
    body: address,
  })
  if (error) throw error
  return data as {
    ok: boolean
    valid: boolean
    messages: Array<{ source: string; code: string; text: string; type: string }>
    suggested: ShippingAddress | null
    raw: { street1?: string; city?: string; state?: string; zip?: string }
  }
}

export async function purchaseShippingLabel(params: {
  type: 'outbound' | 'return'
  rateId: string
  from: ShippingAddress
  to: ShippingAddress
  parcel?: ShippingParcel
  checkoutId?: number
  equipmentId?: number
  idempotencyKey?: string
  createReturn?: boolean
  projectName?: string
  purpose?: 'poc' | 'test' | 'tech_eval' | 'maintenance'
  consigneeContactId?: number
  shippingSameAsConsignee?: boolean
}) {
  const { data, error } = await supabase.functions.invoke('shipping-purchase', {
    body: params,
  })
  // supabase.functions.invoke returns the body as `data` even on non-2xx,
  // but sets `error` for network failures. Check both paths for carrier errors.
  if (error) {
    // Try to extract a structured error message from the response
    const msg =
      (data as any)?.error || (error as any)?.message || String(error)
    throw new Error(msg)
  }
  // The Edge Function returns { ok: false, error: "..." } on carrier errors (e.g. 422)
  if (data && !(data as any).ok && (data as any).error) {
    throw new Error((data as any).error)
  }
  return data as {
    ok: boolean
    shipment: {
      type: string
      carrier: string
      service: string
      tracking_number: string
      label_url: string
      status: string
      cost_cents?: number | null
      currency?: string
      meta: any
    }
    return_shipment?: {
      type: string
      carrier: string
      service: string
      tracking_number: string
      label_url: string
      status: string
      cost_cents?: number | null
      currency?: string
      scan_based?: boolean
      meta?: any
    } | null
  }
}

export type ScanResolveResult = {
  route: 'existing_checked_out' | 'existing_not_checked_out' | 'new_serial'
  equipment_serial_id: number | null
  equipment_type_id: number | null
  checkout_id: number | null
  serial: string | null
  barcode: string | null
  serial_status: string | null
  checkout_status: string | null
  make: string | null
  model: string | null
}

/**
 * Canonical scan resolver. This delegates scan-routing decisions to the database
 * so every client follows the same truth contract.
 */
export async function resolveScannedCode(code: string, source = 'web_scan'): Promise<ScanResolveResult> {
  const { data, error } = await supabase
    .schema('app' as any)
    .rpc('scan_resolve_serial', { p_code: code, p_source: source })
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : null
  if (!row) {
    throw new Error('Scan resolver returned no result')
  }
  return row as ScanResolveResult
}
